/**
 * User Interview Tool - Schema Definitions
 *
 * Zod schemas for the User Interview Tool that allows the orchestrator
 * to ask users clarifying questions with interactive selection interfaces.
 */
import { z } from "zod";
// ═══════════════════════════════════════════════════════════════════════════
// QUESTION TYPES
// ═══════════════════════════════════════════════════════════════════════════
/**
 * Question type - determines selection behavior
 * - multiple_choice: User selects ONE option (radio buttons)
 * - multiple_selection: User selects MULTIPLE options (checkboxes)
 */
export const QuestionTypeSchema = z.enum(["multiple_choice", "multiple_selection"]);
/**
 * Single question definition
 */
export const QuestionSchema = z.object({
    /** Unique identifier for this question */
    id: z.string()
        .describe("Unique identifier for this question (e.g., 'excel_file_select', 'sheets_to_update')"),
    /** Question type - single or multi-select */
    type: QuestionTypeSchema
        .describe("Question type: 'multiple_choice' for single selection, 'multiple_selection' for multi-select"),
    /** Question text shown to user */
    prompt: z.string()
        .describe("The question text shown to the user"),
    /** Pre-populated options */
    options: z.array(z.string())
        .min(1, "At least one option required")
        .describe("Array of pre-populated options for the user to choose from"),
    /** Allow user to add their own option (default: true) */
    allowCustom: z.boolean()
        .optional()
        .default(true)
        .describe("If true, user can add their own custom option"),
    /** Allow user to edit existing options (default: true for text, false for file paths) */
    allowEdit: z.boolean()
        .optional()
        .default(true)
        .describe("If true, user can edit option text (file paths are never editable)"),
});
// ═══════════════════════════════════════════════════════════════════════════
// TOOL INPUT/OUTPUT
// ═══════════════════════════════════════════════════════════════════════════
/**
 * Tool input parameters
 */
export const UserInterviewParamsSchema = z.object({
    questions: z.array(QuestionSchema)
        .min(1, "At least one question required")
        .describe("Array of questions to present to the user. Questions are shown one at a time."),
});
/**
 * Single answer from user
 */
export const AnswerSchema = z.object({
    /** Which question this answers */
    questionId: z.string(),
    /** User's response - string for multiple_choice, array for multiple_selection */
    response: z.union([z.string(), z.array(z.string())]),
    /** True if user chose "Let Carousel decide" */
    letCarouselDecide: z.boolean().optional(),
});
/**
 * Tool response status
 */
export const InterviewStatusSchema = z.enum([
    "completed", // User answered all questions
    "cancelled", // Interview was cancelled (validation error, etc.)
    "timeout", // User took too long (5 min timeout)
    "skipped", // User sent a different message instead of answering
]);
/**
 * Tool output
 */
export const UserInterviewResponseSchema = z.object({
    /** User's answers (empty if cancelled/timeout/skipped) */
    answers: z.array(AnswerSchema),
    /** Status of the interview */
    status: InterviewStatusSchema,
    /** Reason for cancellation (if applicable) */
    cancelReason: z.string().optional(),
});
